import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      hasError: true,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-md w-full mx-4 text-center ">
            <div className="bg-white dark:bg-[#0d1828] rounded-lg shadow-lg p-6">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 0h-2m0 0h2m-6 0h6m2 0v2m0 0h-2m-6 0h6m2 0v4m0 0h-2m-6 0h6m2 0v2m0 0h-2" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-900 dark:text-white mb-4">
                The application encountered an unexpected error.
              </p>
              <details className="text-left">
                <summary className="cursor-pointer text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400">
                  Error Details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 dark:bg-[#0d1828] text-gray-900 dark:text-white rounded text-xs">
                  <p className="font-mono mb-2">
                    {this.state.error?.toString()}
                  </p>
                  <p >
                    This error has been logged. Please try refreshing the page or contact support if the problem persists.
                  </p>
                </div>
              </details>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 mt-2 bg-teal-600 hover:bg-teal-700 text-gray-900 dark:text-white rounded-[6px] font-medium transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
